/**
 * Trading-convention palette, DexScreener-inspired. Dark near-black
 * base, vivid mint / red signals, amber for warnings, purple for
 * futures. All colour is reserved for **functional status** — surfaces
 * and text remain monochrome so the signal-vs-noise ratio stays high.
 *
 * Tuned 2026-04 to mirror DexScreener's reference palette:
 *   • Bg darkened ``#0E1014 → #0b0e14``   (slight cool-black shift)
 *   • Mint brightened ``#5EDA9E → #00d78e`` (closer to DS signature)
 *   • Red brightened  ``#E57373 → #ff5777`` (more visible on bg)
 *   • Amber shifted toward DS warning  ``#E3B04B → #f2b553``
 *
 * Text scale targets WCAG AA against ``bg #0E1014`` (2026-06 dark-grey,
 * lifted off #06080b — see reference_ui_principles_verified):
 *   text1 #E9ECEF ~15:1 AAA · text2 #B4BCD2 ~9.9:1 AAA ·
 *   text3 #939DB0 ~6.9:1 AAA · text4 #7C8597 ~4.77:1 AA (floor).
 */
export const C = {
  // Pure-black base (2026-07): owner chose #000 to match koshiktools (a direct
  // arb-spreads competitor that runs on pure black — measured live). Supersedes
  // the 2026-06 #0E1014 dark-grey lift: DexScreener (#17171C) / Hyperliquid
  // (#303030) sit lighter, but koshiktools validates pure black for this genre
  // and it's the owner's call. Text contrast only IMPROVES on a darker bg (every
  // step stays ≥ its #0E1014 ratio), so the ladder below is unaffected. Surfaces
  // stay as-is → cards float a touch more on the black. See reference_ui_principles_verified.
  bg: "#000000",
  // Near-black elevation ladder (2026-07: owner chose the full flat-black
  // theme). Darkened from the old #141821/#191E28/#20262f dark-grey so every
  // panel, toolbar, table header and card reads as black on the pure-#000 base.
  // The 1px C.border/borderHi still delineates each surface, and the residual
  // lift keeps overlays (drawers, tooltips, dropdowns) from fully dissolving.
  surface: "#0A0D12",
  raised: "#10141B",
  // One step up from ``raised`` — hover-elevated chips + the sidebar
  // collapse-button hover. Still inside the near-black 3-step ladder, not a
  // 4th colour.
  surfaceHi: "#171C24",
  border: "rgba(255,255,255,0.05)",
  borderHi: "rgba(255,255,255,0.09)",
  // Faint mint/red row washes — used by Orderbooks rows to tint a
  // whole row by side without breaking the monochrome surface base.
  // Alpha kept lower than ``mintBg``/``redBg`` so the eye reads them
  // as "row tone" rather than "filled cell". Mirrors the ``flash*``
  // pattern (alpha-stepped variants of the same hue).
  mintBgWeak: "rgba(0, 215, 142, 0.06)",
  redBgWeak:  "rgba(255, 87, 119, 0.05)",

  // Buy (positive)
  mint: "#00d78e",
  mintDim: "#00a46e",
  mintBg: "rgba(0,215,142,0.06)",

  // Sell (negative)
  red: "#ff5777",
  redDim: "#cc4561",
  redBg: "rgba(255,87,119,0.06)",

  // Tick-flash backgrounds — shared between PriceStack, SpreadCell and the
  // table's FlashNum so updates flash in the same colour + alpha envelope.
  // 20 % alpha (bumped from 14 %) so the 300 ms fade is clearly visible on
  // the dense table without drowning the digits. Keep in sync with
  // ``mint`` / ``red`` above.
  flashMint: "rgba(0, 215, 142, 0.20)",
  flashRed:  "rgba(255, 87, 119, 0.20)",

  // Blocked / warning — amber
  amber: "#f2b553",
  amberDim: "#b08435",
  amberBg: "rgba(242,181,83,0.06)",

  // Futures
  purple: "#b092d8",
  purpleBg: "rgba(176,146,216,0.08)",

  // ─── Brand layer — synced with landing (added 2026-05-23) ───────────
  // Two NEW slots that exist purely for chrome / CTA / cross-product
  // anchors, NEVER for data. Strict rule of thumb:
  //   * ``mint`` means "a number went up" — never a button background.
  //   * ``tg``   means "click me to open Telegram" — never a metric.
  //   * ``cy``   means "we're live / focused / aware" — chrome only.
  // The functional palette (mint/red/amber/purple) keeps its meaning;
  // these slots add brand-layer cohesion with the marketing landing
  // without polluting the data-color vocabulary.
  //
  // Contrast on bg #0E1014:
  //   tg (#2AABEE) → ~6.1:1  — AA  (CTA backgrounds + button text)
  //   cy (#67e8f9) → ~10.8:1 — AAA (live dot, focus ring, eyebrow text)
  tg: "#2AABEE",                       // Telegram CTA / primary action
  tgD: "#1f9bd9",                      // hover-darken
  tgBg: "rgba(42,171,238,0.10)",       // sidebar CTA bg, login focus tint
  cy: "#67e8f9",                       // live indicator, focus ring, eyebrows
  cyBg: "rgba(103,232,249,0.08)",      // hover tint, cross-product accents

  // Text contrast ladder — third pass 2026-04-23 (operator: trading
  // terminals are worked in hour-long sessions, so every visible text
  // token must hit WCAG AA 4.5:1). text4 was 2.64:1 (sub-AA); now
  // 4.97:1. Hierarchy preserved: text1 > text2 > text3 > text4 remains
  // visually distinct, just no slot reads as "slightly too faint to be
  // sure what it says" anymore. text5 keeps its divider-only role and
  // must not be used for text.
  text1: "#E9ECEF",      // 16.91:1 — AAA  (page-bright headers, prices, spread%)
  text2: "#B4BCD2",      // 10.47:1 — AAA  (body copy, primary labels)
  text3: "#939DB0",      //  7.32:1 — AAA  (metadata: Age, "37 routes", volume)
  text4: "#7C8597",      //  WCAG 5.13:1 on #0E1014 (AA) BUT APCA Lc~36 — too low for small text. Reserve for timestamps, "—" placeholders, counts, off/disabled state. Critical small data (contract addr / fee / liquidity) uses text3 per reference_ui_principles_verified (opt C).
  text5: "#1c2130",      //  1.25:1 — dividers only — NEVER use for text

  // ---------------------------------------------------------------
  // Discrete bucket palettes. Used by tier chips, age-ladder cells,
  // and spread-severity bars so that every component reaches for the
  // same scale instead of inventing its own hex. Add a new bucket
  // here first, then reference it via ``C.<name>`` in the component.
  // ---------------------------------------------------------------

  // Tier ladder — colour applied to filter chips, tier dividers, and
  // anywhere a magnitude badge is shown. VH reuses ``mint`` so the
  // eye already associates the palette with "top-of-book positive".
  tierVH: "#00d78e", // == mint; kept as explicit slot for semantic clarity
  tierH:  "#5bc0eb", // soft cyan — readable, distinct from mint, not SaaS blue
  tierM:  "#f2b553", // == amber
  tierL:  "#6B7486", // == text3 (low-signal, muted)

  // Age / recency ladder — how fresh is the row? Values describe the
  // age-bucket cut-offs in the consuming component.
  freshOk:    "#3acb97", // 0–5 min — equivalent to "just now"
  freshWarm:  "#3a9a72", // 5–30 min — dimmer mint
  freshCold:  "#b08435", // 30–60 min — amber-ish, starting to stale
  freshStale: "#9c5566", // 60 min+ — red-tinted, age is noticeable

  // Spread-severity ladder — how wide is the orderbook spread? Used
  // by the mini-bar under orderbook rows. Tight = mint-leaning,
  // crisis = red. Fixed breakpoints, not quantile-driven.
  spreadNone:   "#3d4a5c", // no data / not applicable
  spreadTight:  "#34c993", // < 0.10 %
  spreadNormal: "#8b95ad", // 0.10–0.20 %
  spreadWide:   "#f2b553", // 0.20–1.00 %
  spreadCrisis: "#ff5777", // ≥ 1.00 %

  // Dex-dump magnitude accent — shared between the per-token badge
  // and the "high" topic colour. Same orange as spreadWide on purpose
  // (same semantic: "attention but not critical").
  magHigh: "#f2b553",

  // Ban-level accent in Mutes — level 1 warning, level 2+ uses red.
  muteWarn:   "#C89050",
  muteBg:     "#0b1120", // near-black, used by the fixed overlay tint

  // ─── Chart / data-viz layer ─────────────────────────────────────────
  // Tokens for the lightweight-charts panes (SpreadChart, FundingChart,
  // candle charts). These were hardcoded as rgba literals duplicated across
  // four chart files; centralised here so a bg/palette shift (like the
  // #06080b→#0E1014 lift) carries through instead of leaving the grid frozen.
  // Chart panes keep the dark-grey #0E1014 (the pre-2026-07 app bg) instead of
  // the pure-black C.bg — the owner wants menu/tables on #000 but the charts a
  // notch lighter so the plot area reads as a distinct surface, not a black
  // void. Used for the lightweight-charts ``layout.background`` + the container.
  chartBg: "#0E1014",
  chartGrid: "rgba(28, 33, 48, 0.5)",           // == text5 (#1c2130) @ .5 — recedes behind data
  // Sign-aware spread-pane fill (BaselineSeries): cyan above the 0 line
  // (positive / actionable), red below. Alpha-stepped like flash*/mintBgWeak.
  spreadFillPos1: "rgba(103, 232, 249, 0.28)",  // == cy near the line
  spreadFillPos2: "rgba(103, 232, 249, 0.02)",  // == cy fading to the 0 baseline
  spreadFillNeg1: "rgba(255, 87, 119, 0.04)",   // == red near the 0 baseline
  spreadFillNeg2: "rgba(255, 87, 119, 0.20)",   // == red deepening downward
} as const;

/**
 * Categorical line palette for the multi-exchange funding chart — one stable
 * colour per exchange (assigned via a name hash in ``colorForExchange``). NOT
 * the functional mint/red vocabulary (those mean up/down); these only tell the
 * ~20 series apart — the sanctioned "discrete bucket palette" use. Muted,
 * institutional, mutually distinct; no SaaS-blue.
 */
export const SERIES_PALETTE = [
  "#5bc0eb", "#00d78e", "#f2b553", "#b092d8", "#ff5777", "#67e8f9",
  "#2AABEE", "#3acb97", "#C0A860", "#E58B73", "#7fb0a0", "#A090D0",
  "#5090E0", "#C0A050", "#9c8fd8", "#6fce9e",
] as const;

/**
 * Map a CEX network label or DEX chain short to a canonical chain key,
 * so that "BSC" and "BEP20" (both chain index 56) dedupe to the same
 * row, and likewise ETH↔ERC20, SOL↔SPL, MATIC↔POLYGON, etc.
 *
 * Returned key is lowercase-short. Unknown labels fall back to
 * ``label.toLowerCase()`` so unrecognized networks still group with
 * themselves.
 */
const NET_TO_CHAIN: Record<string, string> = {
  ETH: "eth",
  ERC20: "eth",
  ETHEREUM: "eth",
  BSC: "bsc",
  BEP20: "bsc",
  POLYGON: "polygon",
  MATIC: "polygon",
  POLYGONZKEVM: "polygon",
  SOL: "sol",
  SPL: "sol",
  SOLANA: "sol",
  ARB: "arb",
  ARBITRUM: "arb",
  OP: "op",
  OPTIMISM: "op",
  BASE: "base",
  AVAX: "avax",
  AVALANCHE: "avax",
  TRON: "tron",
  TRC20: "tron",
  TON: "ton",
  SUI: "sui",
  ZKSYNC: "zksync",
  LINEA: "linea",
  SCROLL: "scroll",
  BLAST: "blast",
  MANTLE: "mantle",
  CRONOS: "cronos",
  STARKNET: "starknet",
  APTOS: "aptos",
  PULSECHAIN: "pls",
  PLS: "pls",
  NEAR: "near",
  FANTOM: "fantom",
  FTM: "fantom",
};

export function netToChain(label: string): string {
  return NET_TO_CHAIN[label.toUpperCase()] ?? label.toLowerCase();
}

/**
 * Canonical display name per chain. Rendered on network chips across
 * both DEX and CEX legs so the same row reads "ERC20" on both sides
 * instead of "ETH" on the DEX badge and "ERC20" on the CEX badge. Only
 * chains whose DEX short differs from the common CEX/network label
 * need an explicit entry — others (POLYGON, BASE, ARBITRUM, SOL, …)
 * already share the same uppercase token across both worlds and fall
 * through the default branch unchanged.
 */
const CHAIN_TO_DISPLAY_NET: Record<string, string> = {
  eth: "ERC20",
  bsc: "BEP20",
  tron: "TRC20",
  pls: "PULSECHAIN",
};

/** Normalize a network label (CEX or DEX short) for visible rendering.
 *  ``ETH`` / ``eth`` / ``Ethereum`` all resolve to ``ERC20``. */
export function normalizeNetDisplay(label: string): string {
  if (!label) return label;
  const chain = netToChain(label);  // "eth" | "bsc" | "polygon" | …
  const canon = CHAIN_TO_DISPLAY_NET[chain];
  return canon ?? label.toUpperCase();
}

export const NETS: Record<string, { sym: string; c: string }> = {
  // CEX-side network labels (BEP20, ERC20, …)
  SOL: { sym: "◎", c: "#A090D0" },
  SPL: { sym: "◎", c: "#80C098" },
  BEP20: { sym: "⬡", c: "#C0A860" },
  ERC20: { sym: "⟠", c: "#8098C0" },
  ONT: { sym: "○", c: "#70A8A0" },
  DCR: { sym: "◈", c: "#80B098" },
  TRC20: { sym: "△", c: "#B07878" },
  MATIC: { sym: "◆", c: "#8868B8" },
  ARB: { sym: "◇", c: "#5A9FC0" },
  OP: { sym: "◐", c: "#C06060" },
  BASE: { sym: "◼", c: "#5090E0" },
  TON: { sym: "△", c: "#5090C8" },
  // DEX chain shorts (when the leg's exchange == chain short name)
  ETH: { sym: "⟠", c: "#8098C0" },
  BSC: { sym: "⬡", c: "#C0A860" },
  POLYGON: { sym: "◆", c: "#8868B8" },
  FANTOM: { sym: "◇", c: "#5A9FC0" },
  AVALANCHE: { sym: "▲", c: "#E57373" },
  AVAX: { sym: "▲", c: "#E57373" },
  ARBITRUM: { sym: "◇", c: "#5A9FC0" },
  OPTIMISM: { sym: "◐", c: "#C06060" },
  ZKSYNC: { sym: "◇", c: "#7080C0" },
  LINEA: { sym: "◇", c: "#5050C0" },
  SCROLL: { sym: "◇", c: "#C0A050" },
  BLAST: { sym: "◇", c: "#C0C050" },
  MANTLE: { sym: "◇", c: "#80A0A0" },
  CRONOS: { sym: "◇", c: "#5050C0" },
  TRON: { sym: "△", c: "#B07878" },
  SUI: { sym: "◐", c: "#5090C8" },
  STARKNET: { sym: "◇", c: "#C0A050" },
  APTOS: { sym: "◐", c: "#80A0A0" },
  PULSECHAIN: { sym: "◇", c: "#A0A0A0" },
  NEAR: { sym: "◇", c: "#80A0A0" },
};
